import React from "react";
import { useState } from "react";
import "../../assets/styles/tags.css";
import { IoPricetagsOutline } from "react-icons/io5";
import { FiEdit2 } from "react-icons/fi";
import { FiCheck, FiX } from "react-icons/fi";
import Modal from "../../components/common/modal";
import { MdDelete } from "react-icons/md";
import { RiDeleteBin5Line } from "react-icons/ri";
import { useViewTags } from "../../hooks/tags/tags.showall";
import { useTagUpdate } from "../../hooks/tags/tags.update";
import { useRemoveTag } from "../../hooks/tags/tags.delete";  
import { useTagStore } from "../../hooks/tags/tags.add";
import { useViewSingleTag } from "../../hooks/tags/tags.show";
import { toast } from "react-hot-toast";
import Pagination from "../../components/common/pagination";
import Swal from "sweetalert2";



const Tags = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tagName, setTagName] = useState("");
  const [tagSlug, setTagSlug] = useState("");
  const [editingTagId, setEditingTagId] = useState(null);
  const [tags, setTags] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isShowingAll, setIsShowingAll] = useState(false);
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const { data: allTags, isLoading: tagsLoading } = useViewTags(
    isShowingAll ? undefined : currentPage,
    isShowingAll ? (totalItemsCount || 10000) : pageSize,
    debouncedSearchTerm,
  );
  const { mutate: createTag, isPending: isCreating } = useTagStore();
  const { mutate: updateTag, isPending: isUpdating } = useTagUpdate();
  const { mutate: deleteTag, isPending: isDeleting } = useRemoveTag();

  React.useEffect(() => {
    if (allTags?.data?.tags) {
      setTags(allTags.data.tags);
      setPagination(allTags.data.pagination || {});
    }
  }, [allTags]);

  React.useEffect(() => {
    if (allTags?.data?.pagination?.totalDocs) {
      setTotalItemsCount(allTags.data.pagination.totalDocs);
    } else if (allTags?.data?.pagination?.totalItems) {
      setTotalItemsCount(allTags.data.pagination.totalItems);
    } else if (allTags?.data?.tags?.length) {
      setTotalItemsCount(allTags.data.tags.length);
    }
  }, [allTags]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const totalPages = Number(pagination.totalPages) || 1;
  const totalItems = Number(pagination.totalItems || pagination.totalDocs || tags.length) || 0;
  const activePage = Number(pagination.currentPage) || currentPage;

  const handleOpenModal = () => {
    setTagName("");
    setTagSlug("");
    setEditingTagId(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTagName("");
    setTagSlug("");
    setEditingTagId(null);
  };

  const handleTagNameChange = (e) => {
    setTagName(e.target.value);
  };

  const handleSubmitTag = async () => {
    if (!tagName.trim()) {
      toast.error("Please enter a tag name");
      return;
    }

    if (!tagSlug.trim()) {
      toast.error("Slug cannot be empty");
      return;
    }

    setIsLoading(true);

    if (editingTagId) {
      // Update existing tag
      if (!editingTagId) {
        toast.error("Error: Tag ID not found");
        setIsLoading(false);
        return;
      }
      updateTag(
        { 
          id: editingTagId, 
          body: { 
            name: tagName.trim(),
            slug: tagSlug.trim()
          } 
        },
        {
          onSuccess: () => {
            toast.success("Tag updated successfully!");
            handleCloseModal();
          },
          onError: (error) => {
            toast.error(error.response?.data?.message || "Failed to update tag");
          },
          onSettled: () => setIsLoading(false),
        }
      );
    } else {
      // Create new tag
      createTag(
        { 
          name: tagName.trim(),
          slug: tagSlug.trim()
        },
        {
          onSuccess: () => {
            toast.success("Tag added successfully!");
            handleCloseModal();
          },
          onError: (error) => {
            toast.error(error.response?.data?.message || "Failed to add tag");
          },
          onSettled: () => setIsLoading(false),
        }
      );
    }
  };

  const handleDeleteTag = (id, tagName) => {
      Swal.fire({
      title: 'Are you sure?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
     deleteTag(id, {
       onSuccess: () => {
         toast.success("Tag deleted successfully!");
       },
       onError: (error) => {
         toast.error(error.response?.data?.message || "Failed to delete tag");
       },
     });
      }
    });
  };

  const handleEditTag = (tag) => {
    const tagId = getTagId(tag);
    if (!tagId) {
      toast.error("Error: Tag ID not found");
      return;
    }
    setEditingTagId(tagId);
    setTagName(tag.name);
    setTagSlug(tag.slug || "");
    setIsModalOpen(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitTag();
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setIsShowingAll(false);
    setCurrentPage(1);
  };

  // Helper function to get tag ID (handles both 'id' and '_id')
  const getTagId = (tag) => tag?.id || tag?._id;

  return (
    <>
      {/* Modal for Adding / Editing Tag */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        title={editingTagId ? "Edit Tag" : "Add New Tag"}
      >
        <div className="modal-tags-container">
          {/* Tag Name Field */}
          <div style={{ marginBottom: "20px" }}>
            <label htmlFor="tagNameInput" style={{ textAlign: "left", display: "block", marginBottom: "8px" }}>
              Tag Name <span style={{ color: "#d32f2f" }}>*</span>
            </label>
            <input
              type="text"
              id="tagNameInput"
              value={tagName}
              onChange={handleTagNameChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter tag name..."
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Tag Slug Field */}
          <div style={{ marginBottom: "20px" }}>
            <label htmlFor="tagSlugInput" style={{ textAlign: "left", display: "block", marginBottom: "8px" }}>
              Tag Slug <span style={{ color: "#d32f2f" }}>*</span>
            </label>
            <input
              type="text"
              id="tagSlugInput"
              value={tagSlug}
              onChange={(e) => setTagSlug(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="tag-slug"
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "10px 12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              marginTop: "30px",
            }}
          >
            <button
              type="button"
              onClick={handleCloseModal}
              className="cancelBtn"
              disabled={isLoading}
              style={{
                padding: "10px 20px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "#f5f5f5",
                color: "#333",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmitTag}
              className="commonButton"
              disabled={isLoading || isCreating || isUpdating}
              style={{
                padding: "10px 20px",
                border: "none",
                borderRadius: "6px",
                backgroundColor: isLoading ? "#cccccc" : "#004996",
                color: "#fff",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              {isLoading ? (editingTagId ? "Updating..." : "Adding...") : (editingTagId ? "Update Tag" : "Add Tag")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Main Tags Section */}
      <div className="body">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap",
            marginBottom: "12px",
          }}
        >
          <div style={{ flex: "1 1 600px", minWidth: "220px" }}>
            <input
              type="text"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <button
            className="commonButton"
            onClick={handleOpenModal}
            disabled={isCreating}
          >
            <IoPricetagsOutline
              size={20}
              color="#ffffff"
              style={{ marginRight: 8 }}
            />
            Add New Tag
          </button>
        </div>

        <h2>
          Tags Management
          {tagsLoading && <span style={{ marginLeft: "10px", fontSize: "14px", color: "#888" }}>Loading...</span>}
        </h2>

        {tags.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#999",
              borderRadius: "8px",
              backgroundColor: "#f9f9f9",
              marginTop: "20px",
            }}
          >
            <IoPricetagsOutline size={40} style={{ marginBottom: "10px", opacity: 0.5 }} />
            <p>No tags found. Click "Add New Tag" to create one.</p>
          </div>
        ) : (
          <div className="tags">
            <ul className="tagLists">
              {tags.map((tag) => (
                <li
                  key={getTagId(tag)}
                  style={{
                    opacity: isDeleting ? 0.6 : 1,
                    transition: "opacity 0.2s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span>{tag.name}</span>
                    {tag.slug && (
                      <small 
                        style={{ 
                          color: "#888", 
                          fontSize: "12px",
                          backgroundColor: "#e8e8e8",
                          padding: "2px 6px",
                          borderRadius: "4px"
                        }}
                      >
                        {tag.slug}
                      </small>
                    )}
                  </div>
                  <div className="tag-actions">
                    <button
                      className="edit-tag-btn"
                      onClick={() => handleEditTag(tag)}
                      aria-label="Edit tag"
                      title="Edit"
                      disabled={isUpdating || isDeleting}
                    >
                      <FiEdit2 size={18} />
                    </button>
                    <button
                      className="remove-tag"
                      onClick={() => handleDeleteTag(getTagId(tag), tag.name)}
                      aria-label="Delete tag"
                      title="Delete"
                      disabled={isDeleting}
                    >
                      <RiDeleteBin5Line size={18} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/* Pagination Controls */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "30px",
              paddingTop: "20px",
              borderTop: "1px solid #e0e0e0",
              flexWrap: "wrap",
              gap: "20px"
            }}>
              {/* Page Size Selector */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ fontSize: "14px", fontWeight: "500", color: "#333" }}>
                  Items per page:
                </label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>

              {(totalPages > 1 || isShowingAll) && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                  <div style={{ fontSize: "13px", color: "#666" }}>
                    {isShowingAll ? (
                      <span>Showing all {totalItems} tags</span>
                    ) : (
                      <>
                        Page <strong>{activePage}</strong> of <strong>{totalPages}</strong>
                        {totalItems ? <span> ({totalItems} total)</span> : null}
                      </>
                    )}
                  </div>
                  {!isShowingAll && (
                    <Pagination
                      currentPage={activePage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )}
                  <button
                    className="commonButton"
                    onClick={() => {
                      setIsShowingAll(!isShowingAll);
                      setCurrentPage(1);
                    }}
                    style={{
                      fontSize: "13px",
                      padding: "6px 16px",
                      height: "32px",
                      borderRadius: "20px",
                      marginTop: "6px",
                    }}
                  >
                    {isShowingAll ? "Show Less (Paginated)" : "Show All"}
                  </button>
                </div>
              )}

            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Tags;
